/**
 * Creature colour data (art §2.1–§2.8): fur palettes, flesh, irises, cloth dyes, metals, heraldic tinctures and the
 * fixed prop-material mids. sRGB hex; `lookFromAppearance` derives the Cennini ramps (art §2.3) in OKLab.
 * Owner: Creature (P1) for the creature subset; the global pigment token list is F3's `src/content/palette.ts`.
 */
import type { FurPaletteId } from './parts';

export interface FurPalette { base: string; stripe: string; belly: string; stroke: string; cov: number; /** calico second stripe */ stripe2?: string }

/** Art §2.5 (+ the species defaults paragraph). Index = the 5-bit palette id of `FUR_PALETTE_IDS`. */
export const FUR_PALETTES: Readonly<Record<FurPaletteId, FurPalette>> = {
  ginger: { base: '#D27A34', stripe: '#9E4A1C', belly: '#F2D3A2', stroke: '#5C2A12', cov: 0.45 },
  greyTabby: { base: '#8C8478', stripe: '#4E4740', belly: '#D8D0C0', stroke: '#2B1D14', cov: 0.5 },
  marginBlue: { base: '#6F86C2', stripe: '#34477F', belly: '#C9D3EC', stroke: '#1B2F7A', cov: 0.55 },
  whiteFur: { base: '#EDE6D6', stripe: '#BDB4A0', belly: '#FBF6EA', stroke: '#6B5E4E', cov: 0.8 },
  sootFur: { base: '#2E2A2A', stripe: '#151212', belly: '#4A4340', stroke: '#6B6158', cov: 0.6 },
  ochreFur: { base: '#C9A04A', stripe: '#8A6A28', belly: '#EFD9A0', stroke: '#5C3A22', cov: 0.5 },
  umberFur: { base: '#7A5235', stripe: '#4A2F1D', belly: '#C9A47E', stroke: '#2B1D14', cov: 0.45 },
  tortie: { base: '#3B2A22', stripe: '#C7672A', belly: '#E8C690', stroke: '#17110D', cov: 0.4 },
  calicoFur: { base: '#F1E8D6', stripe: '#D07A35', stripe2: '#2E2A2A', belly: '#FBF6EA', stroke: '#5A4632', cov: 0.4 },
  siamese: { base: '#E9DCC4', stripe: '#5B3E2E', belly: '#F6EEDD', stroke: '#3A2A20', cov: 0.35 },
  verdigrisFur: { base: '#5FA58C', stripe: '#2E6B58', belly: '#CFE6D8', stroke: '#1E4238', cov: 0.5 },
  vermilionFur: { base: '#C8442A', stripe: '#7E1E12', belly: '#F0B48C', stroke: '#3E0E08', cov: 0.5 },
  goldLeafFur: { base: '#D4A537', stripe: '#9C7424', belly: '#F3D77A', stroke: '#5C3A22', cov: 0.3 },
  sketch: { base: '#F1E4C3', stripe: '#2B1D14', belly: '#FBF5E4', stroke: '#2B1D14', cov: 0.7 },
  dogUmber: { base: '#7A5235', stripe: '#C9A04A', belly: '#F7F1E1', stroke: '#2B1D14', cov: 0.45 },
  hareBrown: { base: '#B08A5E', stripe: '#7A5A3A', belly: '#EADBC0', stroke: '#3A2A20', cov: 0.5 },
  foxRed: { base: '#C8642A', stripe: '#5C2E16', belly: '#F2E2C8', stroke: '#3E1E0E', cov: 0.45 },
  pigFlesh: { base: '#E2A493', stripe: '#C47F72', belly: '#F0C4B4', stroke: '#8E5A4A', cov: 0 },
  goosePlumage: { base: '#EDE6D6', stripe: '#AEB4C6', belly: '#FBF6EA', stroke: '#2B1D14', cov: 0.35 },
  owlPlumage: { base: '#8A6440', stripe: '#4A2F1D', belly: '#E8D4A8', stroke: '#2B1D14', cov: 0.45 },
  ratGrey: { base: '#8A8078', stripe: '#5E5650', belly: '#C9C0B4', stroke: '#2B1D14', cov: 0.5 },
  goatCream: { base: '#D8CCB4', stripe: '#A89878', belly: '#F1E8D6', stroke: '#5A4632', cov: 0.45 },
  donkeyGrey: { base: '#8C8478', stripe: '#5E564C', belly: '#EDE6D6', stroke: '#2B1D14', cov: 0.45 },
  bearBrown: { base: '#6B4A30', stripe: '#4A2F1D', belly: '#C9A04A', stroke: '#2B1D14', cov: 0.5 },
  lionOchre: { base: '#C9A04A', stripe: '#8A5A28', belly: '#EFD9A0', stroke: '#5C3A22', cov: 0.45 },
  snailBody: { base: '#A8B39A', stripe: '#7A8160', belly: '#D2D6C0', stroke: '#4A5040', cov: 0.3 },
  toadGreen: { base: '#8A9A6B', stripe: '#24594A', belly: '#D8B98A', stroke: '#24341E', cov: 0.35 },
};

/** Flesh (nose leather, pads, inner ears, mouth bag; art §2.4). `tint` = the ramp's shadow tint override. */
export const FLESH: readonly { mid: string; tint: 'verdaccio' | 'scene' }[] = [
  { mid: '#D98A8A', tint: 'verdaccio' }, // 0 madder (rose)
  { mid: '#4A3434', tint: 'scene' },     // 1 soot (black)
  { mid: '#7A5235', tint: 'scene' },     // 2 umber (brown)
  { mid: '#C98C80', tint: 'verdaccio' }, // 3 mottled
];
/** Bare-part colour of beaked species (beak, bird feet) — overrides the flesh ramp (art §2.5: goose minium beak/feet). */
export const BARE_COLOR: Readonly<Partial<Record<string, string>>> = { goose: '#E0692E', owl: '#5A4632' };
/** Human-face "carnation" (hairless options; art §2.4). */
export const CARNATION = '#D9A383';

/** Irises (art §2.7). Index = `iris` enum. */
export const IRIS: readonly string[] = ['#C99A2E', '#D9B45A', '#5FA58C', '#3E64B0', '#B5612B', '#2B1D14', '#DAD6CC', '#C8341E'];
/** Cloth dyes (art §2.8). Index = `dye0/dye1` (10–15 reserved → vellum). */
export const DYES: readonly string[] = ['#C8341E', '#2747A8', '#3C8C74', '#7A3B66', '#C9A04A', '#F7F1E1', '#2E2A2A', '#C76B7E', '#E3B23C', '#4F6A8F'];
/** Heraldic tinctures → tokens (creatures §A11.2): or gold, argent leadWhite, gules vermilion, azure lapisLight, vert verdigris,
 *  purpure folium, sable soot, ermine (pattern on leadWhite). */
export const TINCTURES: readonly string[] = ['#D4A537', '#F7F1E1', '#C8341E', '#2747A8', '#3C8C74', '#7A3B66', '#2E2A2A', '#F7F1E1'];
/** Metal trims (§A5.5 «Краски» metal). */
export const METALS: readonly string[] = ['#D4A537', '#C9CBD1', '#6E7078', '#B5612B'];

/** Fixed mids of the prop materials (art §4.2-C/D), used by previews and portraits; the engine owns the shaders. */
export const MATERIAL_MID: Readonly<Record<number, string>> = {
  2: '#F7F1E1',  // sclera = leadWhite (unshaded)
  4: '#2B1D14',  // pupil / ink
  9: '#7A4E2C',  // leather
  10: '#9A6B3F', // wood
  11: '#6E7078', // iron
  12: '#D4A537', // gold leaf
  14: '#F1E4C3', // parchment
  15: '#B8AC92', // stone
  16: '#F7F1E1', // horn / claw / teeth
  18: '#B9925A', // shell (spiral stripe #7A5A3A)
  19: '#B5612B', // copper leaf
  20: '#C9CBD1', // silver leaf
};
export const SHELL_STRIPE = '#7A5A3A';

/** Scene shadow tints (art §2.3) for `lookFromAppearance(dna, shadowTint)`. */
export const SHADOW_TINTS = {
  arena: { hex: '#26306B', k: 0.22, dLmax: 0.16 },
  constructor: { hex: '#4A4A5A', k: 0.12, dLmax: 0.16 },
  dungeon: { hex: '#0F1C4D', k: 0.4, dLmax: 0.2 },
  map: { hex: '#8A6A3F', k: 0.15, dLmax: 0.16 },
} as const;
export const VERDACCIO = '#7A8160';
export const LEAD_WHITE = '#F7F1E1';
